import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import { Checkbox } from '../../ui/checkbox';

const PlanNumberSection = ({
  formData,
  errors,
  handleInputChange,
  planSuggestionsRef,
  handlePlanNumberSearch,
  handlePlanKeyDown,
  showPlanSuggestions,
  planNumberSuggestions,
  selectedSuggestionIndex,
  handlePlanNumberSelect,
  isDark,
  optional = false
}) => {
  const [isPlanTypeOpen, setIsPlanTypeOpen] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(false);
  const planTypeOptions = ['SP', 'DP', 'CA'];


  return (
    <div className={`border-none rounded-[16px] p-[20px] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.11)] w-full max-w-[822px] flex flex-col gap-[20px] transition-colors duration-200 ${
      isDark ? 'bg-[#000000]' : 'bg-white'
    } ${errors.planNumber || errors.planNumberSuggestion ? 'border-[#FF0000]' : ''}`}>
      <div className="flex flex-col gap-[16px]">
        <div className="flex items-center justify-between">
          <h4 className={`m-0 font-semibold text-[16px] transition-colors duration-200 ${
            isDark ? 'text-white' : 'text-[#072837]'
          }`}>
            Plan Number {optional ? <span className={`font-normal text-[14px] ${isDark ? 'text-[#b3b3b3]' : 'text-[#686868]'}`}></span> : <span className="text-[#FF0000]">*</span>}
          </h4>
          <label className="flex items-center gap-[8px] cursor-pointer select-none">
            <Checkbox
              checked={includeInactive}
              onCheckedChange={(checked) => {
                setIncludeInactive(checked);
                if (formData.planNumber && formData.planNumber.length >= 2) {
                  handlePlanNumberSearch(formData.planNumber, checked);
                }
              }}
              isDark={isDark}
            />
            <span className={`text-[13px] font-normal transition-colors duration-200 ${isDark ? 'text-[#b3b3b3]' : 'text-[#686868]'}`}>
              Include Inactive Plans
            </span>
          </label>
        </div>

        <div className="relative w-full" ref={planSuggestionsRef}>
          <div className={`border-none rounded-[8px] p-[12px] flex items-center gap-[16px] w-full transition-colors duration-200 focus-within:border-solid focus-within:border-[1px] focus-within:border-[#CD6D2C] ${
            isDark ? 'bg-[#161515]' : 'bg-[#f5f5f5] border-solid border-[1px] border-[f8f9fa] '
          }`}>
            {/* Plan Type Dropdown */}
            <DropdownMenu open={isPlanTypeOpen} onOpenChange={setIsPlanTypeOpen}>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className={`flex items-center gap-[6px] shrink-0 font-medium text-[16px] bg-transparent border-0 outline-none cursor-pointer hover:opacity-80 transition-opacity transition-colors duration-200 ${
                    isDark ? 'text-[#b3b3b3]' : 'text-[#686868]'
                  }`}
                >
                  <span>{formData.planType || 'SP'}</span>
                  <ChevronDown 
                    className={`w-[12px] h-[12px] transition-transform transition-colors duration-200 ${
                      isPlanTypeOpen ? 'rotate-180' : ''
                    } ${isDark ? 'text-[#b3b3b3]' : 'text-[#686868]'}`}
                  />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent 
                align="start" 
                className={`min-w-[80px] rounded-[8px] p-0 shadow-lg transition-colors duration-200 ${
                  isDark ? 'bg-[#161515] [border:1px_solid_#292929]' : 'bg-white [border:1px_solid_#d9d9d9]'
                }`}
              >
                {planTypeOptions.map((option) => (
                  <DropdownMenuItem
                    key={option}
                    className={`font-medium text-[16px] px-[12px] py-[8px] cursor-pointer focus:bg-transparent group transition-colors duration-200 ${
                      formData.planType === option 
                        ? isDark 
                          ? 'bg-[#cd6d2c] text-[#000000]' 
                          : 'bg-[#e9ecef] text-[#072837]'
                        : isDark 
                          ? 'bg-[#161515] hover:bg-[#292929] text-white' 
                          : 'bg-white hover:!bg-[#072837] hover:!text-white text-[#072837]'
                    }`}
                    onClick={() => {
                      handleInputChange('planType', option);
                      setIsPlanTypeOpen(false);
                    }}
                  >
                    {option}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Divider */}
            <div className={`w-[1px] h-[30px] shrink-0 transition-colors duration-200 ${
              isDark ? 'bg-[#292929]' : 'bg-[#d9d9d9]'
            }`}></div>

            {/* Plan Number Input */}
            <input
              className={`flex-1 bg-transparent border-0 outline-none text-[16px] transition-colors duration-200 ${
                isDark 
                  ? 'text-white placeholder:text-[#b3b3b3]' 
                  : 'text-[#686868] placeholder:text-[#686868]'
              }`}
              type="text"
              placeholder="Enter Plan no."
              value={formData.planNumber || ''}
              onChange={(e) => handlePlanNumberSearch(e.target.value, includeInactive)}
              onKeyDown={handlePlanKeyDown}
            />
          </div>

          {/* Suggestions List */}
          {showPlanSuggestions && planNumberSuggestions.length > 0 && (
            <div className={`absolute w-full top-full left-0 mt-[2px] max-h-[240px] overflow-y-auto rounded-[4px] z-[1000] shadow-md transition-colors duration-200 ${
              isDark ? 'bg-[#161515] [border:1px_solid_#292929]' : 'bg-white [border:1px_solid_#ced4da]'
            }`}>
              {planNumberSuggestions.map((suggestion, index) => (
                <div
                  key={index}
                  className={`px-3 py-2 font-medium text-[14px] cursor-pointer group transition-colors duration-200 flex items-center gap-2 ${
                    index === selectedSuggestionIndex 
                      ? isDark 
                        ? 'bg-[#cd6d2c] text-[#000000]' 
                        : 'bg-[#e9ecef] text-[#072837]'
                      : isDark 
                        ? 'bg-[#161515] hover:bg-[#292929] text-white' 
                        : 'bg-white hover:!bg-[#072837] hover:!text-white text-[#072837]'
                  }`}
                  onClick={() => handlePlanNumberSelect(suggestion)}
                >
                  <span className="flex-1">
                    {suggestion.plan_number} | {suggestion.database_name.replace(/_/g, ' ').toUpperCase()} | {suggestion.address.trim()}
                  </span>
                  {suggestion.is_inactive && (
                    <span className="ml-2 shrink-0 text-[11px] font-semibold px-[6px] py-[2px] rounded-[4px] bg-[#fff5eb] text-[#cd6d2c]">
                      Unmanaged
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlanNumberSection;

